import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { CheckIcon } from "@heroicons/react/24/outline"
import { store } from "aldoo-ra/Store"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

// The single feature surfaced on the secondary panel.
const MAX_SIZE_FEATURE_ID = "max-transfer-size"

// Plan-specific value (param) of a feature, by feature id.
function featureValue(features, featureId, plan) {
  const feature = features?.find((f) => f.id === featureId || f._id === featureId)
  if (!feature?.plan_values) return null
  const entry = feature.plan_values.find((pv) => {
    const pvId = typeof pv.plan === "string" ? pv.plan : pv.plan?._id
    return pvId === plan._id
  })
  return { name: feature.name, value: entry?.param || null }
}

// Resolve the monthly/annual pricing for a plan from the store data.
function planPricing(plan, storeData, billing) {
  const productId = typeof plan.product === "string" ? plan.product : (plan.product?.item || plan.product?._id)
  const product = storeData.products?.[productId]
  const priceId = product?.price?._id || product?.price || product?.meta?.prices?.monthly
  const storePrice = storeData.prices?.[priceId]

  const byInterval = (interval) =>
    storePrice?.price_model?.find((m) => m.phases?.[0]?.interval === interval) ?? null

  const monthlyModel = byInterval("month")
  const annualModel = byInterval("year")
  const monthlyAmount = monthlyModel?.amount ?? null
  const annualAmount = annualModel?.amount ?? null
  const annualPerMonth = annualAmount != null ? Math.round((annualAmount / 12) * 100) / 100 : null
  const displayAmount = billing === "annual" && annualPerMonth != null ? annualPerMonth : monthlyAmount
  const selectedModel = billing === "annual" ? (annualModel || monthlyModel) : (monthlyModel || annualModel)
  const currency = selectedModel?.currency || "USD"

  return { product, storePrice, selectedModel, displayAmount, annualAmount, currency }
}

export default function SecondaryPricePlan({ plan, billing, storeData, texts, activePid, features }) {
  const { product, storePrice, selectedModel, displayAmount, currency } = planPricing(plan, storeData, billing)
  const feature = featureValue(features, MAX_SIZE_FEATURE_ID, plan)
  const isCurrent = activePid && product?.pid === activePid

  const handleBuy = () => {
    if (!selectedModel || !storePrice) return
    const webEntry = selectedModel.real_product_id?.find((r) => {
      const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
      return platform === "Web"
    })
    store.addToCart({
      product: {
        pid: product?.pid,
        product_id: storePrice._id,
        store_id: webEntry?.id || null,
        name: product?.name || plan.name,
        description: product?.description,
        currency,
        price_model: [{ ...selectedModel, primary: true }],
        duration: billing === "annual" ? texts?.perYear : texts?.perMonth,
        thumbnail: product?.thumbnail,
      },
      proceedToCart: true,
    })
  }

  return (
    <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6 rounded-3xl border border-primary/20 bg-gradient-to-br from-paper/[0.2] via-paper to-paper/[0.7] px-6 py-5">
      {/* Icon */}
      {product?.thumbnail && (
        <div className="w-12 h-12 rounded-[10px] overflow-hidden shrink-0">
          <img
            src={cmsUrl(product.thumbnail)}
            alt={plan.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Name */}
      <div className="text-center sm:text-left shrink-0 sm:min-w-[120px]">
        <Typography 
          variant="txt1_dim" 
          className="block text-center md:text-left"
        >
          {texts?.otherPlans}
        </Typography>

        <Typography 
          variant="h4" 
          className="text-text text-center md:text-left my-0"
        >
          {plan.name}
        </Typography>
      </div>

      {/* Feature */}
      {feature?.value && (
        <div className="flex items-center gap-2 text-sm text-text flex-1 justify-center sm:justify-start">
          <CheckIcon className="w-4 h-4 text-success stroke-[3] shrink-0" />
          <span>
            <span className="text-text_secondary">{feature.name}</span>{" "}
            <span className="font-semibold">{feature.value}</span>
          </span>
        </div>
      )}

      {/* Price */}
      <div className="text-center md:text-right shrink-0">
        {displayAmount != null && (
          <>
            <Typography 
              component="span" 
              variant="h4" 
              className="my-0"
            >
              ${displayAmount}
            </Typography>

            <span className="text-text_secondary text-sm ml-1">
              {texts?.perMonth}
            </span>

            <Typography variant="txt2_dim" className="block">
              {billing === "annual"
                ? (texts?.billedAnnually)
                : (texts?.billedMonthly)}
            </Typography>
          </>
        )}
      </div>

      {/* CTA */}
      <div className="text-center shrink-0 w-full md:w-auto md:ml-auto md:self-center">
        {isCurrent ? (
          <Button 
            variant="primary" 
            className="w-full md:w-auto opacity-80 cursor-default bg-success hover:bg-success" 
            disabled
          >
            {texts?.currentPlan}
          </Button>
        ) : (
          <Button 
            variant="secondary" 
            className="w-full md:w-auto px-8" 
            onClick={handleBuy}
          >
            {texts?.continue || plan.cta_label || texts?.buy}
          </Button>
        )}

        {texts?.individualUseOnly && (
          <p className="text-xs text-text_secondary mt-1">
            {texts?.individualUseOnly}
          </p>
        )}
      </div>
    </div>
  )
}
